import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowLeft, ChevronRight, LogOut, MapPin } from "lucide-react";
import PreMatchWizard from "@/components/umpire/PreMatchWizard";
import LiveScoring from "@/components/umpire/LiveScoring";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Scorecard({ ref_, onBack, backLabel }) {
  const { tid, cid, mid } = ref_;
  const [data, setData] = useState(null);
  const [launching, setLaunching] = useState(false);

  const load = useCallback(() => {
    api.get(`/umpire/scorecard?tid=${tid}&cid=${cid}&mid=${mid}`)
      .then(({ data }) => setData(data)).catch(() => setData(false));
  }, [tid, cid, mid]);
  useEffect(() => { load(); }, [load]);

  if (data === null) return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (data === false) return (
    <div data-testid="scorecard">
      <button data-testid="scorecard-back" onClick={onBack} className="text-sm flex items-center gap-2 mb-5" style={{ color: "var(--text-2)" }}><ArrowLeft className="w-4 h-4" /> {backLabel}</button>
      <p className="text-center text-sm py-24" style={{ color: "var(--text-2)" }}>Could not load scorecard.</p>
    </div>
  );

  const sc = data.scorecard;
  const gameStarted = !!(sc && (sc.team_a_score || sc.team_b_score || sc.started_at || sc.finalized));
  const setupDone = !!sc?.setup?.completed || gameStarted;

  const launch = async ({ first_server_team, setup }) => {
    setLaunching(true);
    try {
      const { data: res } = await api.post(`/umpire/scorecard/setup`, { tid, cid, mid, first_server_team, setup });
      setData((d) => ({ ...d, scorecard: res }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save setup"); }
    finally { setLaunching(false); }
  };

  if (!setupDone) {
    return (
      <div data-testid="scorecard">
        <button data-testid="scorecard-back" onClick={onBack} className="text-sm flex items-center gap-2 mb-4" style={{ color: "var(--text-2)" }}><ArrowLeft className="w-4 h-4" /> {backLabel}</button>
        <PreMatchWizard aName={data.a_name} bName={data.b_name} courtName={data.court_name} onLaunch={launch} launching={launching} />
      </div>
    );
  }

  return <LiveScoring ref_={ref_} onBack={onBack} backLabel={backLabel} />;
}

export default function UmpireScore() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [me, setMe] = useState(null);
  const [tid, setTid] = useState(null);
  const [matchList, setMatchList] = useState(null);
  const [active, setActive] = useState(null);

  const signOut = async () => {
    try { await logout(); } catch (e) { /* ignore */ }
    nav("/login", { replace: true });
  };

  useEffect(() => {
    api.get("/umpire/me").then(({ data }) => {
      setMe(data);
      if (data.kind === "pin" && data.assignments[0]) {
        setActive(data.assignments[0]);
      }
    }).catch(() => setMe(false));
  }, []);

  const loadMatches = useCallback((t) => {
    setTid(t); setMatchList(null);
    api.get(`/umpire/matches?tid=${t}`).then(({ data }) => setMatchList(data.matches)).catch(() => setMatchList([]));
  }, []);

  // fast poll the match list so newly-ready matches appear without a refresh
  useEffect(() => {
    if (!tid || active) return;
    const timer = setInterval(() => {
      api.get(`/umpire/matches?tid=${tid}`).then(({ data }) => setMatchList(data.matches)).catch(() => {});
    }, 2000);
    return () => clearInterval(timer);
  }, [tid, active]);

  if (me === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (me === false) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <div className="text-center" data-testid="umpire-noauth">
        <ShieldCheck className="w-10 h-10 mx-auto text-primary-c opacity-60" />
        <h2 className="font-display font-bold text-xl mt-4">No umpire session</h2>
        <button onClick={() => nav("/umpire")} className="btn-glow rounded-full px-5 py-2.5 mt-5 font-bold">Enter a PIN</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen cyber-grid" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <header className="border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary-c" />
          <span className="font-display font-bold">Umpire Console</span>
          <span className="ml-auto text-xs glass rounded-full px-3 py-1" style={{ color: "var(--text-2)" }} data-testid="umpire-name">{me.name}</span>
          {me.kind === "account" && (
            <button data-testid="umpire-logout" onClick={signOut}
              className="text-xs glass rounded-full px-3 py-1 flex items-center gap-1.5 hover:scale-105 transition-transform" style={{ color: "var(--text-2)" }}>
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          )}
        </div>
      </header>
      <div className="max-w-2xl mx-auto px-4 py-8 grain">
        {active ? (
          <Scorecard ref_={active} backLabel={me.kind === "pin" ? "Sign out" : "Back to matches"}
            onBack={() => { if (me.kind === "pin") { nav("/umpire"); } else { setActive(null); } }} />
        ) : me.kind === "account" && !tid ? (
          <div data-testid="umpire-tournaments">
            <h1 className="font-display font-bold text-2xl mb-4">Your tournaments</h1>
            {me.assignments.length === 0 ? <p className="text-sm" style={{ color: "var(--text-2)" }}>You haven't been assigned to any tournaments yet.</p> :
              <div className="space-y-2">
                {me.assignments.map((a) => (
                  <button key={a.tid} data-testid={`ump-tournament-${a.tid}`} onClick={() => loadMatches(a.tid)}
                    className="w-full text-left rounded-2xl glow-border p-4 flex items-center justify-between hover:scale-[1.005] transition-transform" style={{ background: "var(--surface)" }}>
                    <span className="font-bold">{a.tournament_name}</span><ChevronRight className="w-4 h-4 text-primary-c" />
                  </button>
                ))}
              </div>}
          </div>
        ) : (
          <div data-testid="umpire-match-list">
            <button onClick={() => setTid(null)} className="text-sm flex items-center gap-2 mb-4" style={{ color: "var(--text-2)" }}><ArrowLeft className="w-4 h-4" /> Tournaments</button>
            <h1 className="font-display font-bold text-2xl mb-4">Select a match to score</h1>
            {matchList === null ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary-c" /></div> :
              matchList.length === 0 ? <p className="text-sm" style={{ color: "var(--text-2)" }}>No ready matches. Ask the organizer to generate brackets.</p> :
              <div className="space-y-2">
                {matchList.map((m) => (
                  <button key={m.mid} data-testid={`ump-match-${m.mid}`} onClick={() => setActive(m)}
                    className="w-full text-left rounded-2xl glow-border p-4 flex items-center justify-between hover:scale-[1.005] transition-transform" style={{ background: "var(--surface)" }}>
                    <div><div className="font-bold text-sm">{m.a_name} vs {m.b_name}</div><div className="text-[10px] mono flex items-center gap-1.5" style={{ color: "var(--text-2)" }}>{m.court_name && <span className="flex items-center gap-1 text-primary-c"><MapPin className="w-3 h-3" />{m.court_name} ·</span>}{m.category_label}{m.winner_name ? ` · done: ${m.winner_name}` : ""}</div></div>
                    <ChevronRight className="w-4 h-4 text-primary-c" />
                  </button>
                ))}
              </div>}
          </div>
        )}
      </div>
    </div>
  );
}
